import { useApolloClient } from '@apollo/react-hooks';
import { getTaskStatusesQuery } from '@msales/shared/graphql/queries/task/getTaskStatuses';
import { isAppLockedQuery } from '@msales/shared/graphql/queries/task/isAppLocked';
import {
  getTasksQuery,
  getTasksQueryOptions,
} from '@msales/shared/graphql/queries/taskN/getTasks';
import { getAllTasksDocumentsQuery } from '@msales/shared/graphql/queries/taskN/getTasksDocuments';
import { getAllTasksProductsQuery } from '@msales/shared/graphql/queries/taskN/getTasksProducts';
import { getAllDistributionResultsQuery } from '@msales/shared/graphql/queries/taskN/getAllDistributionResults';
import moment from 'moment';
import { useEffect } from 'react';
import {
  useAuthContext,
  useLocalForageContext,
  useOfflineModeContext,
} from '../context';

import { notificationManager } from '../utils/notificationManager';
import { reportSyncError, reportSyncRecovery } from '../utils/sentryHelpers';
import { clearRasterModificationsFromStorage } from './useTaskModifications';
import { tasksStore, TASK_STORAGE_KEYS } from '../utils/taskUtils';

export const useTasksOfflineData = () => {
  const {
    loadingTasks: loading,
    setLoadingTasks: setLoading,
    refetchTasks: refetch,
    setRefetchTasks: setRefetch,
  } = useLocalForageContext();

  const { authUser } = useAuthContext();
  const { networkState } = useOfflineModeContext();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const FetchProcess = async () => {
      if (!refetch) await new Promise(r => setTimeout(r, 5000));

      const shouldAsync = async (refetch) => {
        try {
          const createdAt = await tasksStore.getItem('createdAt');

          if (createdAt && !refetch) {
            const nodes = await tasksStore.getItem('nodes');
            if (!nodes) {
              await tasksStore.removeItem('createdAt');
              console.warn('[AsyncTasks] No task data found, forcing re-sync');
              reportSyncRecovery('tasks', 'createdAt-without-data');
            }
          }

          const freshCreatedAt = await tasksStore.getItem('createdAt');
          if (
            (refetch ||
              !freshCreatedAt ||
              (false &&
                moment().diff(
                  moment(freshCreatedAt, 'DD/MM/YYYY HH:mm:ss'),
                  'minutes',
                ) >= 20)) &&
            (!networkState || networkState.online) &&
            authUser &&
            !loading
          ) {
            return true;
          }
          if (refetch) setRefetch(false);
          return false;
        } catch (err) {
          console.error('Error in shouldAsync:', err);
          if (refetch) setRefetch(false);
          return false;
        }
      };

      const AsyncTasks = async () => {
        try {
          setLoading(true);
          notificationManager.startSync('tasks', 'Zadaci');

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 60000);

          try {
            const [
              { data, error },
              { data: taskStatusesData, error: taskStatusesError },
              { data: articlesData, error: articlesError },
              { data: documentsData, error: documentsError },
              { data: distributionData, error: distributionError },
              { data: alData },
            ] = await Promise.all([
              apolloClient.query({
                ...getTasksQueryOptions(10000, 0, { status: 'svi' }),
                query: getTasksQuery,
                fetchPolicy: 'no-cache',
                context: { fetchOptions: { signal: controller.signal } },
              }),
              apolloClient.query({ query: getTaskStatusesQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } }),
              apolloClient.query({ query: getAllTasksProductsQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } }),
              apolloClient.query({ query: getAllTasksDocumentsQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } }),
              apolloClient.query({ query: getAllDistributionResultsQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } }),
              apolloClient.query({ query: isAppLockedQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } }),
            ]);

            if (error) throw new Error(error.message);
            if (taskStatusesError) throw new Error(taskStatusesError.message);
            if (articlesError) throw new Error(articlesError.message);
            if (documentsError) throw new Error(documentsError.message);
            if (distributionError) throw new Error(distributionError.message);

            clearTimeout(timeoutId);

            await Promise.all([
              tasksStore.setItem('nodes', data?.tasks?.nodes),
              tasksStore.setItem('tasksStatuses', taskStatusesData.taskStatuses),
              tasksStore.setItem('articlesNodes', articlesData.allTaskProducts),
              tasksStore.setItem('documentsNodes', documentsData.allTaskDocuments),
              tasksStore.setItem(TASK_STORAGE_KEYS.DISTRIBUTION_RESULTS, distributionData.allDistributionResults),
              tasksStore.setItem('appLocked', alData?.isAppLocked),
            ]);
            await tasksStore.setItem(
              'createdAt',
              moment().format('DD/MM/YYYY HH:mm:ss'),
            );

            // Drop local task overrides only after fetched server data confirms them.
            await clearRasterModificationsFromStorage(data?.tasks?.nodes || []);

            notificationManager.completeSync('tasks', true);
          } catch (err) {
            clearTimeout(timeoutId);
            console.error('Error in AsyncTasks:', err);
            reportSyncError(err, 'tasks', networkState);

            let errorMessage = 'Greška prilikom učitavanja zadataka!';
            if (err.name === 'AbortError' || controller.signal.aborted) {
              errorMessage = 'Sinhronizacija zadataka trajala predugo. Provjerite konekciju i pokušajte ponovo.';
            } else if (err.networkError?.statusCode === 400) {
              errorMessage = 'Neispravni podaci za učitavanje zadataka';
            } else if (err.networkError?.statusCode >= 500) {
              errorMessage = 'Greška na serveru prilikom učitavanja zadataka';
            } else if (err.message?.includes('Firebase ID token')) {
              errorMessage = 'Greška autentifikacije';
            }

            notificationManager.completeSync('tasks', false, errorMessage);
          }
        } finally {
          setLoading(false);
          setRefetch(false);
        }
      };

      const callback = async (refetch) => {
        try {
          const should = await shouldAsync(refetch);
          if (should) {
            await AsyncTasks();
          }
        } catch (err) {
          console.error('Error in callback:', err);
        }
      };

      try {
        await callback(refetch);
        // setInterval(() => callback(refetch), 180000);
      } catch (err) {
        console.error('Error in FetchProcess:', err);
      }
    };

    FetchProcess();

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, authUser]);

  return null;
};
